"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const types_1 = require("@upflowy/types");
const helper_1 = require("./helper");
const node_factory_1 = __importDefault(require("./node-factory"));
const xstate_path_mapper_1 = require("./xstate-path-mapper");
/**
 * Remove all undefined properties recursively from obj
 *
 * @param obj
 */
function cleanUndefined(obj) {
    if (Array.isArray(obj)) {
        return obj
            .map((value) => (value && typeof value === 'object' ? cleanUndefined(value) : value))
            .filter((value) => value !== undefined);
    }
    if (typeof obj === 'object' && obj !== null) {
        return Object.entries(obj)
            .map(([key, value]) => [
            key,
            value && typeof value === 'object' ? cleanUndefined(value) : value,
        ])
            .reduce((previousValue, [key, value]) => ({
            ...previousValue,
            ...(value !== undefined && { [key]: value }),
        }), {});
    }
    return obj;
}
const mapAbVariationToCond = (name, variation) => `abtests['${name}']==='${variation === null || variation === void 0 ? void 0 : variation.name}'`;
function mapSplitNode(context, { node, level, index, previousNodeId, pathNextNodeId, }) {
    const splitMeta = node.type === 'abTestNode'
        ? {
            type: 'abTest',
            variations: node.variations,
            name: node.name,
        }
        : {
            type: 'transient',
        };
    const endFound = node.paths.every((p) => (0, xstate_path_mapper_1.hasEndNode)(p.path));
    const joinNodeId = node.joinNode === types_1.ORIGIN_STATE_ID || !node.joinNode ? (0, helper_1.shortUuid)() : node.joinNode;
    const conditions = node.paths.map(({ path: p, cond, name, meta, id }, ind) => ({
        cond: node.type === 'abTestNode' ? mapAbVariationToCond(node.name, node.variations[ind]) : cond,
        target: p[0] ? p[0].id : joinNodeId,
        meta: {
            ...meta,
            name,
            id,
        },
    }));
    const isFirstState = level === 0 && index === 0;
    return {
        endFound,
        states: [
            {
                id: node.id,
                ...(isFirstState ? { on: { FLOW_TRIGGER: conditions } } : { always: conditions }),
                ...(node.type === 'abTestNode' && {
                    entry: {
                        type: 'startAbTest',
                        name: node.name,
                        variations: node.variations,
                    },
                }),
                meta: splitMeta,
            },
            ...node.paths.flatMap((p) => mapNode(context, p.path, joinNodeId, previousNodeId, level + 1)),
            ...(!endFound
                ? [
                    {
                        id: joinNodeId,
                        meta: {
                            type: 'join',
                            splitStateId: node.splitNode,
                        },
                        ...(pathNextNodeId && {
                            always: pathNextNodeId,
                        }),
                    },
                ]
                : []),
        ],
    };
}
function mapNode(context, path, nextNodeId, prevNodeId, level = 0) {
    let endFound = false;
    return path
        .map((node, index) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (endFound)
            return [];
        const previousNodeId = (_b = (_a = path[index - 1]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : prevNodeId;
        const pathNextNodeId = (_d = (_c = path[index + 1]) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : nextNodeId;
        switch (node.type) {
            case 'stepNode':
                return {
                    id: node.id,
                    meta: {
                        type: 'fullpage',
                        ...(((_e = node.config) === null || _e === void 0 ? void 0 : _e.stepId) && { stepId: node.config.stepId }),
                    },
                    on: {
                        ...(pathNextNodeId && {
                            SUBMIT: pathNextNodeId,
                            BACK: previousNodeId,
                            DISMISS: types_1.ORIGIN_STATE_ID,
                        }),
                    },
                };
            case 'condNode':
            case 'abTestNode': {
                const result = mapSplitNode(context, {
                    node,
                    index,
                    previousNodeId,
                    pathNextNodeId,
                    level,
                });
                endFound = result.endFound;
                return result.states;
            }
            case 'externalNode':
                return {
                    id: node.id,
                    meta: {
                        type: 'external',
                    },
                    ...(pathNextNodeId && {
                        on: {
                            FLOW_TRIGGER: pathNextNodeId,
                        },
                    }),
                };
            case 'transient':
                return {
                    id: node.id,
                    ...(pathNextNodeId && {
                        always: pathNextNodeId,
                    }),
                };
            case 'endNode':
                endFound = true;
                return {
                    id: node.id,
                    ...(((_f = node.config) === null || _f === void 0 ? void 0 : _f.action) !== 'redirect' && { always: types_1.ORIGIN_STATE_ID }),
                    meta: {
                        type: 'end',
                        redirectUrl: (_g = node.config) === null || _g === void 0 ? void 0 : _g.redirectUrl,
                        action: (_h = node.config) === null || _h === void 0 ? void 0 : _h.action,
                    },
                };
            default:
                throw new Error(`Invalid node type ${node}`);
        }
    })
        .flat(3);
}
function flowPathToXstate(path, config = {
    id: 'flow',
    stateMap: {},
}) {
    var _a;
    const context = {
        entryActions: [],
    };
    // Ensure an ORIGIN_STATE_ID state is added if it doesn't exist
    const states = mapNode(context, ((_a = path[0]) === null || _a === void 0 ? void 0 : _a.id) === types_1.ORIGIN_STATE_ID ? path : [node_factory_1.default.origin(), ...path])
        .filter((node, index) => node.id !== types_1.ORIGIN_STATE_ID || index === 0)
        .reduce((previousValue, currentValue) => {
        var _a, _b;
        return ({
            ...previousValue,
            ...config.stateMap[(_a = currentValue.id) !== null && _a !== void 0 ? _a : ''],
            [(_b = currentValue.id) !== null && _b !== void 0 ? _b : '']: currentValue,
        });
    }, {});
    return cleanUndefined({
        id: config.id,
        initial: types_1.ORIGIN_STATE_ID,
        entry: context.entryActions,
        states,
    });
}
exports.default = flowPathToXstate;
//# sourceMappingURL=flow-path-to-xstate.js.map